import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import helpContent, { getPageKey } from '../config/helpContent';

export default function HelpPanel() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { i18n } = useTranslation();

  const lang = (i18n.language || 'en').startsWith('ka') ? 'ka' : 'en';
  const pageKey = getPageKey(location.pathname);
  const content = helpContent[lang][pageKey] ?? helpContent[lang]['general'];

  return (
    <>
      {/* Toggle button — fixed bottom-right */}
      <button
        onClick={() => setOpen((v) => !v)}
        title={lang === 'ka' ? 'დახმარება' : 'Help'}
        className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-indigo-600 text-white shadow-lg flex items-center justify-center hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
        </svg>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sliding panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-96 max-w-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-indigo-50">
          <div>
            <p className="text-xs text-indigo-500 font-medium uppercase tracking-wide mb-0.5">
              {lang === 'ka' ? 'სახელმძღვანელო' : 'Guide'}
            </p>
            <h2 className="text-base font-semibold text-gray-900">{content.title}</h2>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {content.intro && (
            <p className="text-sm text-gray-600 leading-relaxed">{content.intro}</p>
          )}

          {content.sections.map((section, idx) => (
            <div key={idx}>
              {section.heading && (
                <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">
                  {section.heading}
                </h3>
              )}
              <ul className="space-y-2">
                {section.items.map((item, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700 leading-relaxed">
                    <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
